import {useEffect} from 'react';
import isEqual from 'react-fast-compare';

import {
  AnalysisGraphQL,
  GetAnalysisPipelineStep,
  isParameterOptimizationStepType,
} from 'analysis-tool/AnalysisTypes';
import {getNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import getDefaultMainViewItemForAnalysis from 'analysis-tool/ui/getDefaultMainViewItemForAnalysis';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  isStepAwareAnalysisItem,
  isViewStepConfigAnalysisItem,
  STEP_TYPE_TO_VIEW_STEP_CONFIG_VIEW_TYPE,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {
  getDatasetSidebarItemData,
  getSaveToResultsSidebarItemData,
  getParameterOptimizationConfigurationSidebarItemData,
  getExportDataCatalogDatasetSidebarItemData,
  getSendToDocumentSidebarItemData,
} from 'analysis-tool/ui/sidebar/SidebarItemDatas';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

/**
 * For a selected item that is a config type, return if it is a valid item for the step.
 */
function isViewStepConfigSelectedItemValidForStep(
  selectedItem: AnalysisMainViewItem,
  step: GetAnalysisPipelineStep
): boolean {
  if (!isViewStepConfigAnalysisItem(selectedItem)) {
    return false;
  }
  return (
    STEP_TYPE_TO_VIEW_STEP_CONFIG_VIEW_TYPE[step.definition.stepType] === selectedItem.type &&
    step.config != null
  );
}

function getValidSelectedItemForDatasetStep(
  analysis: AnalysisGraphQL,
  internalSelectedItem: AnalysisMainViewItem | null,
  selectedStep: GetAnalysisPipelineStep,
  dataFrameId?: string
): AnalysisMainViewItem {
  let selectedData;

  const outputDatasetStepDatas = selectedStep.outputStepDatas.filter(
    (stepData) => stepData.kind === PipelineStepDataKind.DATASET
  );

  if (outputDatasetStepDatas.length >= 1) {
    if (
      internalSelectedItem != null &&
      internalSelectedItem.type === AnalysisMainViewItemType.DATA_FRAME &&
      [PipelineStepStatusCode.RUNNING, PipelineStepStatusCode.BLOCKED].includes(selectedStep.statusCode)
    ) {
      return internalSelectedItem;
    } else if (outputDatasetStepDatas.some((stepData) => stepData.dataFrames.length > 0)) {
      // If there are any output step datas with data frames, then we should always select one of them.
      // If any contain the provided dataFrameId, then return that one.
      // Otherwise, fall back to the first one with any data frame.
      selectedData = outputDatasetStepDatas
        .flatMap((stepData) =>
          stepData.dataFrames.map((dataFrame) => ({outputStepData: stepData, dataFrame}))
        )
        .find(({dataFrame}) => dataFrame.id === dataFrameId);
      if (selectedData == null) {
        const firstOutputStepDataWithDataFrames = outputDatasetStepDatas.find(
          (stepData) => stepData.dataFrames.length > 0
        )!;
        selectedData = {
          outputStepData: firstOutputStepDataWithDataFrames,
          dataFrame: firstOutputStepDataWithDataFrames.dataFrames[0],
        };
      }
    }
  }

  return {
    // If both the internal selected item and the dataset sidebar item are null, then display an empty analysis view
    type: AnalysisMainViewItemType.EMPTY_ANALYSIS,
    ...internalSelectedItem,
    ...getDatasetSidebarItemData(analysis, {
      step: selectedStep,
      ...(selectedData != null ? {data: selectedData} : {}),
    })?.mainViewItem,
  };
}

/**
 * Return a valid selected item based on `internalSelectedItem`.
 *
 * Returned `selectedItem` is determined by evaluating `internalSelectedItem`'s validity in these case:
 * - `internalSelectedItem` is `null` - return the analysis's default item.
 * - `internalSelectedItem` is not step-aware, this is always valid, return it.
 * - `internalSelectedItem` is step-aware.
 *    - The selected step is not in the analysis - return the analysis's default item.
 *    - The selected item type is `RUNNING` but the step has completed - return the analysis's default item.
 *    - The selected view item is a valid configuration view for the selected step - return the item as is
 *    - The selected step corresponds to a top-level file - return item as is.
 *    - The selected step corresponds to a top-level dataset.
 *      - If the item is valid for that step, return it as is.
 *      - Otherwise (e.g. item is `FAILED`, but step is `RUNNING`), return the correct item.
 *    - The selected step corresponds to an ancestor of a top-level dataset.
 *      - Return the correct view item for the last step in that top-level dataset's lineage.
 *
 * @see getDefaultMainViewItemForAnalysis for details on what the analysis's default item is.
 */
function getValidSelectedItem(
  internalSelectedItem: AnalysisMainViewItem | null,
  analysis: AnalysisGraphQL
): AnalysisMainViewItem {
  if (internalSelectedItem == null) {
    return getDefaultMainViewItemForAnalysis(analysis);
  } else if (isStepAwareAnalysisItem(internalSelectedItem)) {
    const selectedStep = getNullableStepFromAnalysis(analysis, internalSelectedItem.stepId);
    const dataFrameId =
      internalSelectedItem.type === AnalysisMainViewItemType.DATA_FRAME
        ? internalSelectedItem.dataFrameId
        : undefined;

    if (selectedStep == null) {
      return getDefaultMainViewItemForAnalysis(analysis);
    } else {
      if (
        internalSelectedItem.type === AnalysisMainViewItemType.RUNNING &&
        [PipelineStepStatusCode.FAILED, PipelineStepStatusCode.SUCCEEDED].includes(selectedStep.statusCode) &&
        !selectedStep.outputStepDatas.some(
          (stepData) => stepData.dataFrames?.length > 0 || stepData.files?.length > 0
        )
      ) {
        return getDefaultMainViewItemForAnalysis(analysis);
      }

      if (selectedStep.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION) {
        return getParameterOptimizationConfigurationSidebarItemData(analysis, selectedStep).mainViewItem;
      }

      if (
        isParameterOptimizationStepType(selectedStep.definition.stepType) ||
        internalSelectedItem.type === AnalysisMainViewItemType.FILE ||
        selectedStep.definition.stepType === PipelineStepType.REGRESSION
      ) {
        return internalSelectedItem;
      }

      if (isViewStepConfigSelectedItemValidForStep(internalSelectedItem, selectedStep)) {
        return internalSelectedItem;
      }

      if (selectedStep.definition.stepType === PipelineStepType.SAVE_TO_RESULTS) {
        return getSaveToResultsSidebarItemData(selectedStep).mainViewItem;
      }

      if (selectedStep.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET) {
        return getExportDataCatalogDatasetSidebarItemData(selectedStep).mainViewItem;
      }

      if (selectedStep.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT) {
        return getSendToDocumentSidebarItemData(selectedStep).mainViewItem;
      }

      return getValidSelectedItemForDatasetStep(analysis, internalSelectedItem, selectedStep, dataFrameId);
    }
  }

  return internalSelectedItem;
}

/**
 * Global state tracking what item is currently selected for an analysis if any.
 *
 * The state can contain either a concrete item or null. Initially, it is null and we map that to a concrete
 * item in the hook body utilizing the selectedStepId url query parameter if present. We expose that concrete
 * item as the "selected item" to the hook caller. This allows for an initial item value that is dynamic
 * based on the analysis rather than always being something like EMPTY_ANALYSIS.
 *
 * @package
 */
export const selectedItemGlobalState = makeUseGlobalClientState<AnalysisMainViewItem | null>(null);

export interface UseSelectedAnalysisItemOutput {
  selectedItem: AnalysisMainViewItem;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void;
}

/**
 * Hook to capture an item selected for display in the main view of the Analysis UI
 */
export default function useSelectedAnalysisItem(
  clientStateId: string,
  analysis: AnalysisGraphQL
): UseSelectedAnalysisItemOutput {
  const [initialSelectedItem, setInitialSelectedItem] =
    selectedItemGlobalState.useGlobalClientState(clientStateId);
  const selectedItem = getValidSelectedItem(initialSelectedItem, analysis);

  useEffect(() => {
    if (!isEqual(initialSelectedItem, selectedItem)) {
      setInitialSelectedItem(selectedItem);
    }
  }, [initialSelectedItem, selectedItem, setInitialSelectedItem]);

  return {
    selectedItem,
    onChangeSelectedItem: (item: AnalysisMainViewItem | null) => {
      setInitialSelectedItem(item);
    },
  };
}
